import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/utils";
import { TextField, type TextFieldProps } from "../TextField";
import { DropdownMenu, type DropdownMenuItem } from "../Dropdown/DropdownMenu";

export interface SearchableDropdownItem extends DropdownMenuItem {
  value?: string;
  [key: string]: any;
}

export interface SearchableDropdownProps
  extends Omit<TextFieldProps, "onChange" | "value"> {
  /**
   * Items to display in the dropdown
   */
  items?: SearchableDropdownItem[];
  /**
   * Section heading text
   */
  sectionHeading?: string;
  /**
   * Whether the dropdown is in loading state
   */
  isLoading?: boolean;
  /**
   * Empty state title
   */
  emptyTitle?: string;
  /**
   * Empty state description
   */
  emptyDescription?: string;
  /**
   * Empty state link text
   */
  emptyLinkText?: string;
  /**
   * Empty state link click handler
   */
  onEmptyLinkClick?: () => void;
  /**
   * Primary button text
   */
  primaryButtonText?: string;
  /**
   * Secondary button text
   */
  secondaryButtonText?: string;
  /**
   * Primary button click handler
   */
  onPrimaryClick?: () => void;
  /**
   * Secondary button click handler
   */
  onSecondaryClick?: () => void;
  /**
   * Dropdown menu width
   */
  dropdownWidth?: "auto" | "full";
  /**
   * Show chevron on list items
   */
  showChevron?: boolean;
  /**
   * Custom empty state icon
   */
  emptyIcon?: React.ReactNode;
  /**
   * Disable footer buttons
   */
  disableFooter?: boolean;
  /**
   * Footer layout orientation
   */
  footerLayout?: "horizontal" | "vertical";
  /**
   * Callback when search value changes
   */
  onSearchChange?: (value: string) => void;
  /**
   * Callback when item is selected
   */
  onItemSelect?: (item: SearchableDropdownItem) => void;
  /**
   * Custom filter function
   */
  filterFunction?: (item: SearchableDropdownItem, query: string) => boolean;
  /**
   * Search value (controlled)
   */
  searchValue?: string;
  /**
   * Default search value (uncontrolled)
   */
  defaultSearchValue?: string;
  /**
   * Custom class for dropdown menu
   */
  dropdownClassName?: string;
  /**
   * Minimum characters before showing dropdown
   */
  minSearchLength?: number;
  /**
   * Show dropdown on focus
   */
  showOnFocus?: boolean;
}

const defaultFilter = (
  item: SearchableDropdownItem,
  query: string
): boolean => {
  const searchQuery = query.toLowerCase();
  return (
    item.title.toLowerCase().includes(searchQuery) ||
    (item.description?.toLowerCase().includes(searchQuery) ?? false)
  );
};

export const SearchableDropdown = React.forwardRef<
  HTMLInputElement,
  SearchableDropdownProps
>(
  (
    {
      className,
      items = [],
      sectionHeading,
      isLoading = false,
      emptyTitle = "No Search Results Found",
      emptyDescription = "Add description of what the user can search for here.",
      emptyLinkText = "Link to support site",
      onEmptyLinkClick,
      primaryButtonText = "Primary",
      secondaryButtonText = "Secondary",
      onPrimaryClick,
      onSecondaryClick,
      dropdownWidth = "full",
      showChevron = false,
      emptyIcon,
      disableFooter = false,
      footerLayout = "horizontal",
      onSearchChange,
      onItemSelect,
      filterFunction = defaultFilter,
      searchValue: controlledSearchValue,
      defaultSearchValue = "",
      dropdownClassName,
      minSearchLength = 0,
      showOnFocus = true,
      containerClassName,
      ...textFieldProps
    },
    ref
  ) => {
    const [uncontrolledSearchValue, setUncontrolledSearchValue] =
      React.useState(defaultSearchValue);
    const [isOpen, setIsOpen] = React.useState(false);
    const [focusedIndex, setFocusedIndex] = React.useState(-1);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const menuRef = React.useRef<HTMLDivElement>(null);
    const [position, setPosition] = React.useState({
      top: 0,
      left: 0,
      width: 0,
    });

    React.useImperativeHandle(ref, () => inputRef.current!);

    // Update position when dropdown opens or window resizes
    React.useEffect(() => {
      if (isOpen && dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      }
    }, [isOpen]);

    const searchValue =
      controlledSearchValue !== undefined
        ? controlledSearchValue
        : uncontrolledSearchValue;

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (controlledSearchValue === undefined) {
        setUncontrolledSearchValue(newValue);
      }
      onSearchChange?.(newValue);

      // Show dropdown if minimum search length is met
      if (newValue.length >= minSearchLength) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    };

    const handleFocus = () => {
      if (showOnFocus && searchValue.length >= minSearchLength) {
        setIsOpen(true);
      }
    };

    const handleItemSelect = (item: SearchableDropdownItem) => {
      onItemSelect?.(item);
      if (controlledSearchValue === undefined) {
        setUncontrolledSearchValue(item.title);
      }
      setIsOpen(false);
      inputRef.current?.focus();
    };

    // Filter items based on search
    const filteredItems = React.useMemo(() => {
      if (!searchValue) return items;
      return items.filter((item) => filterFunction(item, searchValue));
    }, [items, searchValue, filterFunction]);

    // Close dropdown when clicking outside
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node) &&
          menuRef.current &&
          !menuRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }
    }, [isOpen]);

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen) {
        if (e.key === "ArrowDown" || e.key === "Enter") {
          setIsOpen(true);
          e.preventDefault();
        }
        return;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setFocusedIndex((prev) =>
            prev < filteredItems.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case "Enter":
          e.preventDefault();
          if (focusedIndex >= 0 && filteredItems[focusedIndex]) {
            handleItemSelect(filteredItems[focusedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          setFocusedIndex(-1);
          break;
      }
    };

    // Update items with onClick handlers that call handleItemSelect
    const itemsWithHandlers = filteredItems.map((item) => ({
      ...item,
      onClick: () => handleItemSelect(item),
    }));

    const showDropdown = isOpen && searchValue.length >= minSearchLength;

    const dropdownMenu = showDropdown && (
      <div
        ref={menuRef}
        style={{
          position: "absolute",
          top: `${position.top + 8}px`,
          left: `${position.left}px`,
          width: dropdownWidth === "full" ? `${position.width}px` : "auto",
          zIndex: 9999,
        }}
      >
        <DropdownMenu
          items={itemsWithHandlers}
          sectionHeading={sectionHeading}
          isLoading={isLoading}
          isEmpty={filteredItems.length === 0}
          emptyTitle={emptyTitle}
          emptyDescription={emptyDescription}
          emptyLinkText={emptyLinkText}
          onEmptyLinkClick={onEmptyLinkClick}
          primaryButtonText={primaryButtonText}
          secondaryButtonText={secondaryButtonText}
          onPrimaryClick={onPrimaryClick}
          onSecondaryClick={onSecondaryClick}
          showChevron={showChevron}
          emptyIcon={emptyIcon}
          disableFooter={disableFooter}
          footerLayout={footerLayout}
          onClose={() => setIsOpen(false)}
          focusedIndex={focusedIndex}
          className={dropdownClassName}
          width={dropdownWidth === "full" ? "full" : "auto"}
        />
      </div>
    );

    return (
      <>
        <div ref={dropdownRef} className={cn("relative", containerClassName)}>
          <TextField
            ref={inputRef}
            value={searchValue}
            onChange={handleSearchChange}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            containerClassName="mb-0"
            {...textFieldProps}
          />
        </div>

        {/* Dropdown Menu Portal */}
        {typeof document !== "undefined" &&
          dropdownMenu &&
          createPortal(dropdownMenu, document.body)}
      </>
    );
  }
);

SearchableDropdown.displayName = "SearchableDropdown";
